import * as React from 'react';

import {
  enumCurrencyCode,
  enumUnit,
  MoneyInput,
} from '@rematter/frontend-lib-api-client';

import {
  QuoteLineItemData,
  QuoteLineItemUpdate,
} from '../_components/_shared/LineItems/types';

const TEMP_ID_PREFIX = 'temp-';

export const createEmptyQuoteLineItem = (): QuoteLineItemData => ({
  id: `${TEMP_ID_PREFIX}${Date.now()}-${Math.random()}`,
  itemId: null,
  item: null,
  packageFormat: null,
  quantity: 0,
  unit: enumUnit.Pounds,
  unitPrice: { amount: 0, currencyCode: enumCurrencyCode.USD },
  totalPrice: { amount: 0, currencyCode: enumCurrencyCode.USD },
  notes: null,
});

export const QUOTE_TEMP_ID_PREFIX = TEMP_ID_PREFIX;

const computeTotalPrice = (
  quantity: number,
  unitPrice: MoneyInput,
): MoneyInput => ({
  amount: quantity * unitPrice.amount,
  currencyCode: unitPrice.currencyCode,
});

const applyUpdateToQuoteLineItem = (
  lineItem: QuoteLineItemData,
  updates: QuoteLineItemUpdate,
): QuoteLineItemData => {
  const merged = { ...lineItem, ...updates };
  const quantity =
    updates.quantity !== undefined ? updates.quantity : lineItem.quantity;
  const unitPrice =
    updates.unitPrice !== undefined ? updates.unitPrice : lineItem.unitPrice;

  return {
    ...merged,
    totalPrice: computeTotalPrice(quantity, unitPrice),
  };
};

type UseQuoteLineItemsManagerOptions = {
  lineItems: QuoteLineItemData[];
  onLineItemsChange: (items: QuoteLineItemData[]) => void;
};

export const useQuoteLineItemsManager = ({
  lineItems,
  onLineItemsChange,
}: UseQuoteLineItemsManagerOptions) => {
  const addLineItem = React.useCallback(() => {
    onLineItemsChange([...lineItems, createEmptyQuoteLineItem()]);
  }, [lineItems, onLineItemsChange]);

  const updateLineItem = React.useCallback(
    (id: string, updates: QuoteLineItemUpdate) => {
      const updatedItems = lineItems.map((li) => {
        if (li.id !== id) return li;
        return applyUpdateToQuoteLineItem(li, updates);
      });
      onLineItemsChange(updatedItems);
    },
    [lineItems, onLineItemsChange],
  );

  const deleteLineItem = React.useCallback(
    (id: string) => {
      onLineItemsChange(lineItems.filter((li) => li.id !== id));
    },
    [lineItems, onLineItemsChange],
  );

  return {
    addLineItem,
    updateLineItem,
    deleteLineItem,
  };
};
